import { apiRequest } from '../utils/api';
import type { ListingCardListing } from '../ListingCard';
import type { AreaInterest } from '../AppContent/NewLandingPage/CouldBuyFlow/areaInterestApi';

export type ActivitySection = 'my_places' | 'buying_interests' | 'following';

export interface MyPlaceRow {
  willingness_id: string;
  building_id: string;
  address: { street?: string; houseNumber?: string; city?: string } | null;
  image: string | null;
  /** All buyers interested in this place — not only the unseen ones. */
  buyer_count: number;
  new_buyer_count: number;
}

export type BuyingInterestRow = {
  confirmed_listing_count?: number;
  unconfirmed_listing_count?: number;
} & (
  | {
      kind: 'area';
      area_interest_id: string;
      display_areas: unknown;
      criteria: unknown;
      /** Anchor building address, labelling the circle ("<street> ± 500 m"). */
      anchor_address: {
        street?: string;
        houseNumber?: string;
        city?: string;
      } | null;
      /** All listings matching this interest — not only the unseen ones. */
      listing_count: number;
      new_listing_count: number;
    }
  | {
      kind: 'building';
      willingness_id: string;
      building_id: string;
      address: unknown;
      /** All listings on this building — not only the unseen ones. */
      listing_count: number;
      new_listing_count: number;
    }
);

export interface FollowingRow {
  building_id: string;
  address: unknown;
  like_count: number;
  /** Listings and buyers on this building, the viewer's own included — a
   *  followed row describes the building, not who is courting the viewer. */
  listing_count: number;
  buyer_count: number;
  new_count: number;
  /** Likes on this building the user hasn't seen yet — not its all-time total. */
  new_like_count: number;
}

export interface ActivityResponse {
  my_places: MyPlaceRow[];
  buying_interests: BuyingInterestRow[];
  following: FollowingRow[];
  total_new: number;
}

// Fetch the assembled Activity sections + nav-badge total.
export const fetchActivity = (): Promise<ActivityResponse> =>
  apiRequest('/activity');

// Every buyer/listing behind one Activity row. Both keys are always present and
// empty when the section doesn't use them: my_places returns buyers, buying
// interests return listings, following returns both.
export interface ActivityMatches {
  /** The row's own listing, when one backs it — drives the header manage menu.
   *  Null for area interests and followed buildings, which the user doesn't own. */
  subject: ListingCardListing | null;
  /** Set instead of `subject` on area-interest rows, so their manage menu can
   *  edit the interest in place. */
  area_interest: AreaInterest | null;
  buyers: ListingCardListing[];
  listings: ListingCardListing[];
  /** Present for the paged v2 buying-interest response. */
  match_ids?: string[];
  next_cursor?: string | null;
  listing_count?: number;
  new_listing_count?: number;
  confirmed_listing_count?: number;
  unconfirmed_listing_count?: number;
}

// Fetch the matches behind one Activity row, for the row detail view.
export const fetchActivityMatches = (
  section: ActivitySection,
  entityId: string,
  cursor?: string,
): Promise<ActivityMatches> =>
  apiRequest(
    `/activity/matches?section=${section}&entity_id=${encodeURIComponent(entityId)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
  );

// Clear one row's "new" badge after the user taps it.
export const markActivitySeen = (
  section: ActivitySection,
  entityId: string,
  matchIds?: string[],
): Promise<void> =>
  apiRequest('/activity/seen', {
    method: 'POST',
    body: {
      section,
      entity_id: entityId,
      ...(matchIds ? { match_ids: matchIds } : {}),
    },
  });
